import { useState } from 'react';
import {
  Sun,
  Droplets,
  Ruler,
  Calendar,
  AlertTriangle,
  ShieldCheck,
  Info,
  Eye,
  Trash2
} from 'lucide-react';
import { usePlant } from '../../../context/PlantContext';
import { useCommand } from '../../../context/CommandContext';
import { useGardenSettings } from '../../../context/GardenSettingsContext';
import { ModifyObjectCommand } from '../../../commands/ModifyObjectCommand';
import { FLOWER_COLOR_MAP } from '../../../constants/flowerColors';
import PlantDetailDialog from './PlantDetailDialog';
import ConfirmDialog from '../../ui/ConfirmDialog';
import HardinessWarningBadge from './HardinessWarningBadge';
import SunlightWarningBadge from './SunlightWarningBadge';
import CustomSizeSlider from './CustomSizeSlider';
import { compareZones } from '../../../utils/hardinessZone';
import { useShadowHeatmap } from '../../../context/ShadowHeatmapContext';
import { zoneAt } from '../../../scene3d/shadow/shadowAggregator';
import { plantsApi } from '../../../services/api';
import {
  SUNLIGHT_LABELS,
  WATER_REQUIREMENT_LABELS,
  translateEnum,
  translateEnumList,
} from '../../../utils/plantEnumLabels';
import { getPlantImageUrl } from '../../../utils/plantImageUtils';

/**
 * PlantPropertiesTab - Display of selected plant properties with color picker.
 *
 * Shows detailed plant information including image, properties grid,
 * toxic warnings, flower color picker, and action buttons (Details, Platzieren).
 *
 * @param {Object} props - Component props
 * @param {Object} props.selectedPlant - Plant data object (null if none)
 * @param {Object} props.selectedCanvasObject - PlantCircle canvas object (null if none on canvas)
 * @param {Function} props.onDelete - Callback when the placed plant's delete button is clicked (receives the canvas object)
 */
const PlantPropertiesTab = ({ selectedPlant, selectedCanvasObject, onDelete }) => {
  const { enterPlacementMode } = usePlant();
  const { commandStack } = useCommand();
  const { settings } = useGardenSettings();
  const { grid: sunlightGrid } = useShadowHeatmap();
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailPlant, setDetailPlant] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Empty state when no plant selected
  if (!selectedPlant) {
    return (
      <div className="p-4">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Info className="w-12 h-12 text-gray-400 mb-3" />
          <p className="text-sm text-gray-500">
            Wähle eine Pflanze aus,
            <br />
            um deren Eigenschaften anzuzeigen.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Öffne den Pflanzenkatalog oder
            <br />
            wähle eine Pflanze aus deinen Favoriten.
          </p>
        </div>
      </div>
    );
  }

  // Extract plant properties
  const {
    name,              // API uses 'name', not 'german_name'
    german_name,       // Fallback for compatibility
    scientific_name,
    care_instructions = {},
    properties = {}
  } = selectedPlant;

  const {
    sunlight,
    water_requirement,
    min_height_cm: height_min,
    max_height_cm: height_max,
    min_width_cm: width_min,
    max_width_cm: width_max,
    space_requirement_cm,  // API uses this field name
    spacing_min,           // Legacy fallback
    flowering_months,      // API uses this field name
    bloom_start,           // Legacy fallback
    bloom_end,             // Legacy fallback
    toxic_to_pets,
    toxic_to_children,
    toxicity_level,
    flower_color,
  } = properties;

  // Flower color picker — only shown when canvas plant is selected and has colors
  const flowerColors = Array.isArray(flower_color) ? flower_color.filter(c => FLOWER_COLOR_MAP[c]) : [];
  const hasFlowerColors = selectedCanvasObject && flowerColors.length > 0;
  const currentSelectedColor = selectedCanvasObject?.selectedColor || null;

  // Per-instance size override (#754) — only for a plant placed on the canvas.
  // Sliders are bounded to the DB span [min, max]; a value inside the span
  // concretises "mein Exemplar auf dem bekannten Spektrum" without touching the
  // catalog. Only offered when the span is real (both bounds, max > min).
  const customHeightCm = selectedCanvasObject?.customHeightCm ?? null;
  const customWidthCm = selectedCanvasObject?.customWidthCm ?? null;
  const canOverrideHeight =
    selectedCanvasObject && height_min > 0 && height_max > 0 && height_max > height_min;
  const canOverrideWidth =
    selectedCanvasObject && width_min > 0 && width_max > 0 && width_max > width_min;

  const commitSizeOverride = (key, newValue) => {
    if (!selectedCanvasObject || !commandStack) return;
    const oldValue = selectedCanvasObject[key] ?? null;
    if (oldValue === newValue) return;
    const command = new ModifyObjectCommand(
      selectedCanvasObject,
      { [key]: oldValue },
      { [key]: newValue }
    );
    commandStack.execute(command);
  };

  const handleColorSelect = (colorKey) => {
    if (!selectedCanvasObject || !commandStack) return;
    const oldColor = selectedCanvasObject.selectedColor;
    if (oldColor === colorKey) return;
    const command = new ModifyObjectCommand(
      selectedCanvasObject,
      { selectedColor: oldColor },
      { selectedColor: colorKey }
    );
    commandStack.execute(command);
  };

  // Prefer german_name if available, fallback to name
  const displayName = german_name || name;
  const imageName = name || german_name; // DB 'name' matches image filename

  // Format sunlight display (zentrale Labels, #751)
  const sunlightDisplay =
    translateEnumList(SUNLIGHT_LABELS, sunlight, 'sunlight') || 'Unbekannt';

  // Format water requirement display
  const waterDisplay =
    translateEnum(WATER_REQUIREMENT_LABELS, water_requirement, 'water_requirement') ||
    'Unbekannt';

  // Format height display
  const heightDisplay = (() => {
    if (height_min && height_max) {
      return `${height_min} - ${height_max} cm`;
    } else if (height_min) {
      return `ab ${height_min} cm`;
    } else if (height_max) {
      return `bis ${height_max} cm`;
    }
    return 'Unbekannt';
  })();

  // Format width display
  const widthDisplay = (() => {
    if (width_min && width_max) {
      return `${width_min} - ${width_max} cm`;
    } else if (width_min) {
      return `ab ${width_min} cm`;
    } else if (width_max) {
      return `bis ${width_max} cm`;
    }
    return 'Unbekannt';
  })();

  // Format spacing display (use space_requirement_cm first, fallback to spacing_min)
  const spacingDisplay = space_requirement_cm ? `${space_requirement_cm} cm` : (spacing_min ? `${spacing_min} cm` : 'Unbekannt');

  // Format bloom time (use flowering_months array first, fallback to bloom_start/bloom_end)
  const bloomDisplay = (() => {
    // Check for string 'none' value (some plants may have this)
    if (flowering_months === 'none' || flowering_months === 'None') {
      return 'Keine';
    }

    // Try flowering_months array
    if (flowering_months && Array.isArray(flowering_months)) {
      // Empty array [] or [0] means no flowering (e.g., Buchsbaum has [])
      if (flowering_months.length === 0 || (flowering_months.length === 1 && flowering_months[0] === 0)) {
        return 'Keine';
      }
      const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
      const sortedMonths = [...flowering_months].sort((a, b) => a - b);
      const firstMonth = sortedMonths[0];
      const lastMonth = sortedMonths[sortedMonths.length - 1];
      return `${months[firstMonth - 1]} - ${months[lastMonth - 1]}`;
    }
    // Fallback to bloom_start/bloom_end
    if (bloom_start && bloom_end) {
      const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
      return `${months[bloom_start - 1]} - ${months[bloom_end - 1]}`;
    }
    return 'Unbekannt';
  })();

  // Hardiness zone comparison — warn when the plant's min zone is warmer than the garden's.
  // Mirrors PlantCard/PlantDetailDialog derivation; the badge self-hides on 'ok'/'unknown',
  // so we gate the padded wrapper on the same result to avoid an empty layout gap.
  const plantZone = care_instructions?.min_hardiness_zone || properties?.min_hardiness_zone;
  const gardenZone = settings?.hardinessZone;
  const hardinessResult =
    plantZone && gardenZone ? compareZones(plantZone, gardenZone) : 'unknown';
  const showHardinessWarning =
    hardinessResult !== 'ok' && hardinessResult !== 'unknown';

  // Licht-Abgleich (#606): Zone an der Pflanzposition, sobald eine
  // Jahresübersicht berechnet wurde. Position = Canvas-Mittelpunkt der
  // PlantCircle (Editor-Konvention 1 px = 1 cm → /100 = Meter, Welt-z = Canvas-y).
  const sunlightZone = (() => {
    if (!sunlightGrid || !selectedCanvasObject?.getCenterPoint) return null;
    const center = selectedCanvasObject.getCenterPoint();
    return zoneAt(sunlightGrid, { x: center.x / 100, z: center.y / 100 });
  })();

  // Determine toxicity status
  const isToxic = toxic_to_pets || toxic_to_children;

  const toxicityLevelLabels = {
    low: 'Geringe Giftigkeit',
    medium: 'Mittlere Giftigkeit',
    high: 'Hohe Giftigkeit',
    severe: 'Sehr hohe Giftigkeit',
  };

  // Handle details button click
  const handleDetailsClick = async () => {
    try {
      // Fetch full plant data from API if we have an ID
      if (selectedPlant.id) {
        const fullPlantData = await plantsApi.getById(selectedPlant.id);
        setDetailPlant(fullPlantData);
      } else {
        // Fallback to the plant data we have
        setDetailPlant(selectedPlant);
      }
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch plant details:', error);
      // Fallback to the plant data we have
      setDetailPlant(selectedPlant);
      setDetailDialogOpen(true);
    }
  };

  // Handle detail dialog close
  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false);
    setDetailPlant(null);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Plant Image - 200×200px */}
      <div className="p-4">
        <div className="w-full h-[200px] bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center">
          {imageName ? (
            <img
              src={getPlantImageUrl(imageName, 'detail')}
              alt={displayName || imageName}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="text-center">
              <Info className="w-16 h-16 text-gray-400 mx-auto mb-2" />
              <span className="text-sm text-gray-500">Kein Bild verfügbar</span>
            </div>
          )}
        </div>

        {/* Plant Names */}
        <div className="mt-4">
          <h3 className="text-base font-semibold text-gray-900">
            {displayName || 'Unbekannte Pflanze'}
          </h3>
          {scientific_name && (
            <p className="text-sm italic text-gray-600 mt-1">
              {scientific_name}
            </p>
          )}
        </div>
      </div>

      {/* Properties Grid */}
      <div className="px-4 pb-4 space-y-3">
        {/* Sunlight */}
        <PropertyItem
          icon={<Sun className="w-4 h-4 text-yellow-600" />}
          label="Sonnenlicht"
          value={sunlightDisplay}
        />

        {/* Water */}
        <PropertyItem
          icon={<Droplets className="w-4 h-4 text-blue-600" />}
          label="Wasserbedarf"
          value={waterDisplay}
        />

        {/* Height — shows the instance override when set (#754), else the DB span */}
        <PropertyItem
          icon={<Ruler className="w-4 h-4 text-gray-600" />}
          label="Wuchshöhe"
          value={customHeightCm != null ? `${customHeightCm} cm` : heightDisplay}
        />

        {/* Width — shows the instance override when set (#754), else the DB span */}
        <PropertyItem
          icon={<Ruler className="w-4 h-4 text-gray-600" />}
          label="Wuchsbreite"
          value={customWidthCm != null ? `${customWidthCm} cm` : widthDisplay}
        />

        {/* Spacing */}
        <PropertyItem
          icon={<Ruler className="w-4 h-4 text-gray-600" />}
          label="Pflanzabstand"
          value={spacingDisplay}
        />

        {/* Bloom Time */}
        <PropertyItem
          icon={<Calendar className="w-4 h-4 text-pink-600" />}
          label="Blütezeit"
          value={bloomDisplay}
        />
      </div>

      {/* Hardiness Warning — self-hides when the plant is winter-hardy in the garden zone */}
      {showHardinessWarning && (
        <div className="px-4 pb-4">
          <HardinessWarningBadge
            plantZone={plantZone}
            gardenZone={gardenZone}
            variant="full"
          />
        </div>
      )}

      {/* Licht-Abgleich (#606) — nur wenn eine Jahresübersicht berechnet wurde
          und die Pflanze auf dem Canvas steht; Badge verbirgt sich, wenn es passt */}
      {sunlightZone && (
        <div className="px-4 pb-4">
          <SunlightWarningBadge zone={sunlightZone} sunlight={sunlight} />
        </div>
      )}

      {/* Toxicity Warning/Safe Badge */}
      <div className="px-4 pb-4">
        {isToxic ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-red-900">Giftig</h4>
              <p className="text-xs text-red-800 mt-0.5">
                {toxic_to_pets && toxic_to_children
                  ? 'Giftig für Haustiere und Kinder'
                  : toxic_to_pets
                  ? 'Giftig für Haustiere'
                  : 'Giftig für Kinder'}
              </p>
              {toxicity_level && (
                <p className="text-xs text-red-700 mt-1">
                  {toxicityLevelLabels[toxicity_level] || toxicity_level}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-900 font-medium">
              Ungiftig
            </span>
          </div>
        )}
      </div>

      {/* Flower Color Picker */}
      {hasFlowerColors && (
        <div className="px-4 pb-4">
          <p className="text-xs text-gray-600 mb-2">Blütenfarbe</p>
          <div className="flex flex-wrap gap-2">
            {flowerColors.map((colorKey) => {
              const colorInfo = FLOWER_COLOR_MAP[colorKey];
              const isSelected = currentSelectedColor === colorKey;
              return (
                <button
                  key={colorKey}
                  onClick={() => handleColorSelect(colorKey)}
                  title={colorInfo.name}
                  className={`w-7 h-7 rounded-full transition-all duration-150 ${
                    isSelected
                      ? 'ring-2 ring-offset-2 ring-gray-700 scale-110'
                      : 'ring-1 ring-gray-300 hover:scale-110'
                  }`}
                  style={{
                    backgroundColor: colorInfo.hex,
                    outline: colorInfo.border ? `1px solid ${colorInfo.border}` : undefined,
                  }}
                  aria-label={colorInfo.name}
                  aria-pressed={isSelected}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Eigene Größe (#754) — Instanz-Override innerhalb der DB-Spanne.
          Nur für eine platzierte Pflanze mit echter Spanne. */}
      {(canOverrideHeight || canOverrideWidth) && (
        <div className="px-4 pb-4">
          <div className="border border-gray-200 rounded-lg p-3 space-y-3">
            <p className="text-xs text-gray-600">
              Eigene Größe für dieses Exemplar — etwa bei regelmäßigem Rückschnitt
              oder einer kleiner wachsenden Sorte.
            </p>
            {canOverrideHeight && (
              <CustomSizeSlider
                label="Wuchshöhe"
                min={height_min}
                max={height_max}
                value={customHeightCm}
                onCommit={(v) => commitSizeOverride('customHeightCm', v)}
              />
            )}
            {canOverrideWidth && (
              <CustomSizeSlider
                label="Wuchsbreite"
                min={width_min}
                max={width_max}
                value={customWidthCm}
                onCommit={(v) => commitSizeOverride('customWidthCm', v)}
              />
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="px-4 pb-4 space-y-2 mt-auto">
        {/* Details Button */}
        <button
          onClick={handleDetailsClick}
          className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center gap-2"
          title="Pflanzendetails anzeigen"
        >
          <Eye className="w-4 h-4" />
          Details anzeigen
        </button>

        {/* Place Button */}
        <button
          onClick={() => enterPlacementMode(selectedPlant)}
          className="w-full px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors duration-200"
        >
          Platzieren
        </button>

        {/* Delete Button — only for a plant placed on the canvas. Mirrors the
            garden-object panel so a plant is deletable on touch (iPad), where the
            Entf key isn't available (#749). */}
        {selectedCanvasObject && (
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2
              bg-red-50 border border-red-300 text-red-700 rounded-lg
              hover:bg-red-100 hover:border-red-400 transition-all font-medium text-sm"
            aria-label="Pflanze löschen"
          >
            <Trash2 size={16} />
            Löschen
          </button>
        )}
      </div>

      {/* Plant Detail Dialog */}
      {detailDialogOpen && detailPlant && (
        <PlantDetailDialog
          plant={detailPlant}
          isOpen={detailDialogOpen}
          onClose={handleDetailDialogClose}
        />
      )}

      {/* Confirm Delete Dialog — shares the pattern with DynamicGardenObjectProperties */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onConfirm={() => {
          setShowDeleteDialog(false);
          onDelete?.(selectedCanvasObject);
        }}
        onCancel={() => setShowDeleteDialog(false)}
        title="Pflanze löschen"
        message="Möchtest du diese Pflanze wirklich löschen?"
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        variant="danger"
      />
    </div>
  );
};

/**
 * PropertyItem - Reusable component for property display
 */
const PropertyItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <div className="flex-shrink-0 mt-0.5">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-600">{label}</p>
      <p className="text-sm text-gray-900 font-medium">{value}</p>
    </div>
  </div>
);

export default PlantPropertiesTab;
